<template>
  <NiceView
    :aboveTitle="generalStore.viewHeader"
    :title="CommonService.routes.GROUPS.title"
    :icon="CommonService.routes.GROUPS.icon"
    :loading="loading"
    flexBody
  >
    <!-- Filters -->
    <NiceFilters v-model="filtersList" @change="filterChanged" :showCreateButton="permissions.can_create_guest_groups" />

    <!-- Table -->
    <NiceTable
      :actions="actions"
      :columns="columns"
      :data="data"
      :loading="loading"
      freezeLastColumn
      showFooter
      showLimit
      paginated
      @filterChange="filterChanged"
    />

    <!-- Form -->
    <NicePanel :name="panelName" @close="closeEdit">
      <GroupForm @cancel="closeEdit" @success="successEdit" :data="selectedItem"></GroupForm>
    </NicePanel>

    <!-- <pre>{{ data }}</pre> -->
  </NiceView>
</template>

<script setup>
import { ref, inject, watch, onMounted } from "vue";
import { formatDateWithTime } from "@/plugins/date";
import { isAfter } from "date-fns";

import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import ApiService from "@/services/api/api.service";
import GroupForm from "@/components/forms/GroupForm.vue";

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();
const $getQuery = inject("getQuery");
const $nice = inject("nice");

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const permissions = ref(generalStore.permissions);
const modules = ref(generalStore.modules);

const data = ref([]);
const loading = ref(false);
const selectedItem = ref(null);
const panelName = "event";

// Watch for id change
watch(
  () => route.query,
  () => {
    if (route.query.id) {
      $nice.panel(panelName, true);
    }
  }
);

onMounted(async () => {
  if (route.query.id) {
    $nice.panel(panelName, true);
  }
});

function closeEdit() {
  selectedItem.value = null;
  router.push({ query: { ...route.query, id: undefined } });
  $nice.panel(panelName, false);
}

function successEdit() {
  closeEdit();
  getData();
}

function openEdit(item) {
  selectedItem.value = item;
  router.push({ query: { ...route.query, id: item.id } });
}

async function getData() {
  loading.value = true;
  try {
    const query = await $getQuery();
    data.value = await ApiService.groups.getAll(route.params.organizationId, route.params.eventId, query);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

const filtersList = [];

function filterChanged() {
  getData();
}

const actions = [
  {
    icon: () => "icon-edit-3",
    function: (v) => openEdit(v),
    hidden: () => !permissions.value.can_edit_guest_groups,
  },
];

const columns = [
  {
    name: $gettext("Name"),
    key: "name",
    hasOrder: true,
  },
  {
    name: $gettext("Color"),
    key: "color",
    html: (v) => `<span class="badge" style="background-color: ${v};">${v || $gettext("None")}</span>`,
    hasOrder: true,
  },
  {
    name: $gettext("Allow entry until"),
    key: "allow_entry_until",
    class: "text-right",
    // html: (v) => formatDateWithTime(v),
    html: (v, row) => `<span class="${isAfter(new Date(), row.allow_entry_until) ? "red" : ""}">${formatDateWithTime(v)}</span>`,
    hasOrder: true,
  },
  {
    name: $gettext("Guest limit"),
    key: "guest_limit",
    class: "text-right",
    formatter: (v, row) => (row.enable_guest_limit ? v : "∞"),
    hasOrder: true,
  },
  {
    name: $gettext("Plus one limit per guest"),
    key: "plus_one_limit_per_guest",
    class: "text-right",
    disabled: !modules.value.PLUS_ONES,
    formatter: (v, row) => (row.allow_plus_one ? v : "-"),
    hasOrder: true,
  },
  {
    name: $gettext("Require email"),
    key: "require_email",
    formatter: (v) => (v ? $gettext("Yes") : $gettext("No")),
    hasOrder: true,
  },
  {
    name: $gettext("Require phone number"),
    key: "require_phone_number",
    formatter: (v) => (v ? $gettext("Yes") : $gettext("No")),
    hasOrder: true,
  },
];
</script>
